const DEFAULT_SYNC_INTERVAL = 30;
const STORE_KEY_PREFIX = 'enterprise_contacts_config_';
const POLL_EVENT_ID = 'enterpriseContactsConfigSync';
export class ContactSyncEnterpriseConfig {
    constructor() {
        this.ctx = null;
        this._account = '';
        this.domain = '';
        this.config = null;
        this.pollHandle = null;
        this.currentSyncInterval = DEFAULT_SYNC_INTERVAL;
        this.syncIntervalUpdater = null;
        this.onSyncTrigger = null;
        this._isPolling = false;
    }
    async init(ctx, _account, domain, syncIntervalUpdater, onSyncTrigger) {
        this.ctx = ctx;
        this._account = _account;
        this.domain = domain;
        this.syncIntervalUpdater = syncIntervalUpdater !== null && syncIntervalUpdater !== void 0 ? syncIntervalUpdater : null;
        this.onSyncTrigger = onSyncTrigger !== null && onSyncTrigger !== void 0 ? onSyncTrigger : null;
        await this.loadFromStore();
        await this.doFetch();
    }
    destroy() {
        this.stopPolling();
        this.config = null;
        this.ctx = null;
        this._account = '';
        this.domain = '';
        this.syncIntervalUpdater = null;
        this.onSyncTrigger = null;
    }
    getConfig() {
        return this.config;
    }
    getDomain() {
        return this.domain;
    }
    get isPolling() {
        return this._isPolling;
    }
    async setHasAllContacts(value) {
        if (!this.config) {
            return;
        }
        this.config.hasAllContacts = value;
        await this.saveToStore();
    }
    async setLastUpdateTime(value) {
        if (!this.config) {
            return;
        }
        this.config.lastUpdateTime = value;
        await this.saveToStore();
    }
    async clearStore() {
        if (!this.ctx || !this._account) {
            return;
        }
        try {
            await this.ctx.storeApi.del(this.getStoreKey(), { _account: this._account });
        }
        catch (ex) {
            console.error('[enterprise_contacts_config] clearStore failed', ex);
        }
    }
    async fetchConfig(force) {
        if (force) {
            this.resetVersions();
        }
        await this.doFetch();
    }
    getStoreKey() {
        return `${STORE_KEY_PREFIX}_${this.domain}`;
    }
    async saveToStore() {
        if (!this.ctx || !this.config) {
            return;
        }
        try {
            await this.ctx.storeApi.put(this.getStoreKey(), JSON.stringify(this.config), {
                _account: this._account,
            });
        }
        catch (ex) {
            console.error('[enterprise_contacts_config] saveToStore failed', ex);
        }
    }
    async loadFromStore() {
        if (!this.ctx) {
            return;
        }
        try {
            const result = await this.ctx.storeApi.get(this.getStoreKey());
            if (result.suc && result.data) {
                const parsed = JSON.parse(result.data);
                this.config = parsed;
                console.log('[enterprise_contacts_config] loadFromStore success', parsed);
            }
        }
        catch (ex) {
            console.error('[enterprise_contacts_config] loadFromStore failed', ex);
        }
    }
    getRequestParams() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return {
            domain: this.domain,
            ruleVersion: (_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.ruleVersion) !== null && _b !== void 0 ? _b : 0,
            contactsVersion: (_d = (_c = this.config) === null || _c === void 0 ? void 0 : _c.contactsVersion) !== null && _d !== void 0 ? _d : 0,
            unitVersion: (_f = (_e = this.config) === null || _e === void 0 ? void 0 : _e.unitVersion) !== null && _f !== void 0 ? _f : 0,
            lastUpdateTime: (_h = (_g = this.config) === null || _g === void 0 ? void 0 : _g.lastUpdateTime) !== null && _h !== void 0 ? _h : 0,
        };
    }
    resetVersions() {
        if (this.config) {
            this.config.ruleVersion = 0;
            this.config.contactsVersion = 0;
            this.config.unitVersion = 0;
            this.config.lastUpdateTime = 0;
        }
    }
    async doFetch() {
        var _a, _b, _c;
        if (!this.ctx) {
            return;
        }
        try {
            const url = this.ctx.systemApi.getUrl('getEnterpriseContactsInfo');
            const params = this.getRequestParams();
            const res = await this.ctx.http.get(url, params, { _account: this._account });
            const data = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data;
            if (!data) {
                console.warn('[enterprise_contacts_config] doFetch: empty data');
                return;
            }
            data.hasSearchRule = (_b = data.hasSearchRule) !== null && _b !== void 0 ? _b : 0;
            const prevSyncInterval = this.currentSyncInterval;
            const newSyncInterval = this.normalizeSyncInterval(data.syncInterval);
            const lastUpdateTime = (_c = this.config) === null || _c === void 0 ? void 0 : _c.lastUpdateTime;
            this.config = Object.assign(Object.assign({}, data), { lastUpdateTime });
            this.currentSyncInterval = newSyncInterval;
            await this.saveToStore();
            if (this.syncIntervalUpdater) {
                try {
                    this.syncIntervalUpdater(newSyncInterval);
                }
                catch (ex) {
                    console.error('[enterprise_contacts_config] syncIntervalUpdater error', ex);
                }
            }
            if (prevSyncInterval !== newSyncInterval && this.pollHandle) {
                console.log(`[enterprise_contacts_config] syncInterval changed: ${prevSyncInterval} → ${newSyncInterval}, rebuilding poller`);
                this.stopPolling();
                this.startPolling();
            }
        }
        catch (ex) {
            console.error('[enterprise_contacts_config] doFetch failed', ex);
        }
    }
    normalizeSyncInterval(value) {
        const num = Number(value);
        if (!Number.isFinite(num) || num <= 0) {
            return DEFAULT_SYNC_INTERVAL;
        }
        return num;
    }
    getPollEventId() {
        return `${POLL_EVENT_ID}_${this.domain}_${this._account}`;
    }
    startPolling() {
        if (!this.ctx || this._isPolling) {
            return;
        }
        const interval = this.currentSyncInterval * 6;
        this.pollHandle = {
            eventPeriod: 'mid',
            handler: ob => {
                if (ob.seq > 0 && ob.seq % interval === 0 && this.onSyncTrigger) {
                    try {
                        this.onSyncTrigger(this._account);
                    }
                    catch (ex) {
                        console.error('[enterprise_contacts_config] onSyncTrigger error', ex);
                    }
                }
            },
            id: this.getPollEventId(),
            seq: 0,
        };
        this.ctx.systemApi.intervalEvent(this.pollHandle);
        this._isPolling = true;
        console.log(`[enterprise_contacts_config] startPolling, domain=${this.domain}, account=${this._account}, interval=${this.currentSyncInterval}min`);
    }
    stopPolling() {
        if (!this.ctx || !this.pollHandle) {
            return;
        }
        this.ctx.systemApi.cancelEvent(this.pollHandle.eventPeriod, this.pollHandle.id);
        this.pollHandle = null;
        this._isPolling = false;
        console.log(`[enterprise_contacts_config] stopPolling, account=${this._account}`);
    }
}
